<template>
  <div id="revenueCenterEdit">

    <PageTitle :title="$t('revenue_centers.entity')" :subtitle="$t('revenue_centers.subtitle')">
      <button class="btn btn-primary" @click="save">{{$t('save')}}</button>
    </PageTitle>

    <div class="container" v-if="ready">

      <Panel :title="$t('revenue_centers.information')" collapsible>
        <div class="columns">
          <div class="column">
            <div class="group">
              <LocalizedInput :title="$t('revenue_centers.name')" :translate="revenueCenter.name"/>
            </div>
            <div class="group">
              <LocalizedInput textarea :title="$t('revenue_centers.description')" :translate="revenueCenter.description"/>
            </div>
          </div>
        </div>
      </Panel>

      <Panel :title="$t('revenue_centers.layout')" collapsible>
        <div class="columns">
          <div class="column">
            <div class="group">
              <label>{{$t('functions.choose_role')}}</label>
              <select v-model="selectedRoleId">
                <option :value="null" hidden disabled>{{$t('choose')}}...</option>
                <option :value="role.id" v-for="(role, roleIndex) of roles" :key="`role-${roleIndex}`"> {{role.name}}</option>
              </select>
            </div>
          </div>
          <div class="column">&nbsp;</div>
        </div>
        <br>
        <PosLayout v-for="(layout, layoutIndex) of revenueCenter.layouts" v-show="layout.roles.find(r => r.id === selectedRoleId)"
                   v-model="revenueCenter.layouts[layoutIndex]" :key="`layout-${layoutIndex}`" :functions="functions"></PosLayout>
      </Panel>

      <Panel :title="$t('revenue_centers.catalog_display')" collapsible>
        <label>{{$t('revenue_centers.catalog_display_description')}}</label>
        <br><br>
        <CatalogConfiguration v-model="revenueCenter.catalog"></CatalogConfiguration>
      </Panel>

    </div>

    <PageFooter :backAction="{text: $t('revenue_centers.back'), action: back}" :mainAction="{text: $t('save'), action: save}" v-if="ready">
    </PageFooter>

    <Modal name="copy-prices" :title="$t('revenue_centers.copy_prices')" :subtitle="$t('revenue_centers.copy_prices_description')" :width="500"
           :actions="[{name: $t('revenue_centers.no_copy'), action: noCopy}, {name: $t('revenue_centers.copy'), action: copy, primary: true}]">
      <template v-if="revenueCenters.length > 0">
        <div class="group">
          <label>{{$t('revenue_centers.copy_from_revenue_center')}}</label>
          <select v-model="selectedRevenueCenterRef">
            <option :value="revenueCenter.ref" v-for="(revenueCenter, revenueCenterIndex) of revenueCenters" :key="'rc'+revenueCenterIndex">
              {{$to(revenueCenter.name)}}
            </option>
          </select>
        </div>

      </template>
    </Modal>

  </div>
</template>

<script>
  import LocalizedInput from "@/components/tools/LocalizedInput";
  import PosLayout from "@/components/qsr/PosLayout";
  import PageTitle from "@/components/PageTitle";
  import PageFooter from "@/components/PageFooter";
  import CatalogConfiguration from "@/components/inventory/CatalogConfiguration";
  import {mapActions} from "vuex";

  export default {
    name: "RevenueCenterEdit",

    components: { LocalizedInput, PosLayout, PageTitle, PageFooter, CatalogConfiguration },

    data() {
      return {
        functions: [],
        roles: [],
        selectedRoleId: null,
        ready: false,
        defaultCartTopFunctions: ["item.edit", "item.remove", "remark.add", "price.change", "description.change",
          "recipe", "allergens", "suggestion", "transfer", "transfer.table", "status", "table.quick"],
        defaultCartBottomFunctions: ["quick_close.card", "quick_close.cash", "quit", "save"],
        revenueCenters: [],
        selectedRevenueCenterRef: null,
        revenueCenter: {
          name: { fr: "", en: "" },
          description: { fr: "", en: "" },
          pricesFrom: null,
          catalog: {
            categories: []
          },
          config: {
            services: ["pos"] //TODO
          },
          layouts: []
        }
      }
    },

    computed: {
      companyId() {
        return this.$route.query.company;
      },
      id() {
        return this.$route.query.id;
      },
    },

    async mounted() {
      showSpinner();
      if (this.id) {
        await this.loadRevenueCenter();
      }
      await Promise.all([this.loadFunctions(), this.loadPosRoles(), this.loadRevenueCenters()]);
      this.ready = true;
      hideSpinner();
    },

    methods: {
      ...mapActions(["getPosFunctions", "getPosRoles", "getRevenueCenter", "createRevenueCenter", "saveRevenueCenter", "getRevenueCenters"]),

      async loadRevenueCenter() {
        let response = await this.getRevenueCenter(this.id);
        if (response) {
          this.revenueCenter = response;
        }
      },

      async loadRevenueCenters() {
        let response = await this.getRevenueCenters(this.companyId);
        if (response && response.revenueCenters) {
          this.revenueCenters = response.revenueCenters;
          if (this.revenueCenters.length > 0) {
            this.selectedRevenueCenterRef = this.revenueCenters[0].ref;
          }
        }
      },

      async loadFunctions() {
        let response = await this.getPosFunctions();
        if (response) {
          this.functions = response.functions || [];
        }
      },

      async loadPosRoles() {
        let response = await this.getPosRoles(this.companyId);
        if (response) {
          this.roles = response.posRoles || [];
        }
        for (let role of this.roles) {
          if (!this.revenueCenter.layouts.find(l => l.roles.find(r => r.id === role.id))) {
            this.createLayout(role);
          }
        }
        if (this.roles.length > 0) {
          this.selectedRoleId = this.roles[0].id;
        }
      },

      copy() {
        this.revenueCenter.pricesFrom = this.selectedRevenueCenterRef;
        this.$modal.show("copy-prices", false);
        this.save(true);
      },

      noCopy() {
        this.revenueCenter.pricesFrom = null;
        this.$modal.show("copy-prices", false);
        this.save(true);
      },

      createLayout(role) {
        this.revenueCenter.layouts.push({
          roles: [{id: role.id, name: role.name}],
          cartTop: { cols: 4, rows: 3, list: [...this.defaultCartTopFunctions.map(f => ({ id: f, customData: { name: {fr: "", en: ""} } }))] },
          cartBot: { cols: 4, rows: 1, list: [...this.defaultCartBottomFunctions.map(f => ({ id: f, customData: { name: {fr: "", en: ""} } }))] },
          paymentBot: { cols: 4, rows: 1, list: [] },
          name: role.name
        });
      },

      async save(ignoreModal) {
        let data = JSON.parse(JSON.stringify(this.revenueCenter));
        data.company = this.companyId;

        if (((typeof ignoreModal === "boolean" && !ignoreModal) || typeof ignoreModal !== "boolean")
            && this.revenueCenters.length > 0 && !this.id) {
          this.$modal.show("copy-prices");
          return;
        }

        showSpinner();
        let response = this.id
            ? await this.saveRevenueCenter(data)
            : await this.createRevenueCenter(data);
        hideSpinner();
        if (response) {
          this.revenueCenter = response;
          toast(this.$t("revenue_centers.save_success"), "success", this.$t("success"));
          let editPath = "/revenue-center/edit";
          if (this.revenueCenter.id && this.$route.path !== editPath) {
            await this.$router.replace(this.$router.buildContext(editPath, {id: this.revenueCenter.id}));
          }
        } else {
          toast(this.$t("revenue_centers.save_error"), "error", this.$t("error.title"));
        }

      },

      back() {
        this.$router.push(this.$router.buildContext("/revenue-centers"));
      }
    }
  }
</script>

<style lang="scss" scoped>
  #revenueCenterEdit {
    .container {
      padding-top: 24px;
    }
  }
</style>